import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  ErrorDisplayUnsavedItems,
  ErrorsDisplayContainerStyled,
  ErrorsDisplayItem,
} from "@ryde/controls";
import {
  useConfirmDirtyField,
  useProduceSummary,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  bookingsProductionDrawerConfigurationAtom,
  bookingsSelector,
  BulkProduceTypeEnum,
  companyIdSelector,
  DirectionEnum,
  getDefaultProductionTypes,
  getStoredBookingsProductionAction,
  Log,
  resolveProduceButtonDirection,
  resolveProduceDirectionsServicePayload,
  storeBookingsProductionAction,
  systemSettingsDtoAtom,
  type BookingDto,
  type DatesUnit,
  type IBookingsProductionDrawerConfigurationForm,
  type IProduceJoinPassengerPreviewDto,
  type ITypeormWhereCriteria,
} from "@ryde/shared";

import { DrawerContentStyled } from "../../drawer";
import type { IBookingsProductionStepsComponentProps } from "../bookings-production-steps";
import { configurationFormValidationSchema } from "../configuration-form-validation";
import { BookingsProductionDrawerConfigurationForm } from "./configuration-form";

export const BookingsProductionDrawerConfiguration: FC<
  IBookingsProductionStepsComponentProps
> = ({
  setHandlers,
  setCurrentStep,
  onCloseDrawer,
  bookingsIds,
  direction,
}) => {
  const setBookingsProductionDrawerConfiguration = useSetRecoilState(
    bookingsProductionDrawerConfigurationAtom,
  );
  const { t } = useTranslation();
  const resolver = useYupValidationResolver(configurationFormValidationSchema);
  const { buildProduceSummaryForCustomProductionDialog } = useProduceSummary();
  const bookingDtos = useRecoilValue(bookingsSelector);
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const isHubsLogicEnabled = Boolean(
    systemSettingsDto?.metadata?.enableHubsLogic,
  );
  const bookingDates: DatesUnit | undefined = useMemo(() => {
    const bookingsIdsSet = bookingsIds ? new Set(bookingsIds) : null;
    const bookingsTimeStamps = bookingDtos
      .filter(x => x.bookTime && bookingsIdsSet?.has(x.id))
      .map(x => dayjs.utc(x.bookTime).valueOf());

    if (!bookingsTimeStamps?.length) {
      return undefined;
    }

    const minTimestamp = Math.min(...bookingsTimeStamps);
    const maxTimestamp = Math.max(...bookingsTimeStamps);
    const datesUnit: DatesUnit = {
      date1: dayjs(minTimestamp).toISOString(),
      date2: dayjs(maxTimestamp).toISOString(),
    };
    return datesUnit;
  }, [bookingDtos, bookingsIds]);

  const methods = useForm<IBookingsProductionDrawerConfigurationForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver,
    defaultValues: {
      productionAction: companyId
        ? getStoredBookingsProductionAction(companyId)
        : BulkProduceTypeEnum.Produce,
      productionType: getDefaultProductionTypes(isHubsLogicEnabled),
      direction: direction === DirectionEnum.None ? undefined : direction,
    },
  });
  const {
    getValues,
    formState: { isDirty },
    trigger,
  } = methods;

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });
  const onCloseHandler = applyIfConfirmed({ isDirty })((): void => {
    onCloseDrawer();
  });

  const onSaveClick = useCallback(async () => {
    const validForm = await trigger();

    if (!validForm) {
      return;
    }

    const customProductionFormData = getValues();

    if (!customProductionFormData.productionAction) {
      Log.warnAndToast("rideProduction.selectProductionAction");
      return;
    }

    const criteria: ITypeormWhereCriteria<BookingDto> &
      IProduceJoinPassengerPreviewDto = {
      id: { $in: bookingsIds },
    };

    if (customProductionFormData.direction) {
      criteria.direction = resolveProduceButtonDirection(
        customProductionFormData.direction,
      );
    }

    if (customProductionFormData.shiftIds?.length) {
      criteria.shiftId = { $in: customProductionFormData.shiftIds };
    }

    if (customProductionFormData.unitIds?.length) {
      criteria.passenger = {
        ...(criteria.passenger ?? {}),
        unitId: { $in: customProductionFormData.unitIds },
      };
    }

    if (customProductionFormData.siteIds?.length) {
      criteria.passenger = {
        ...(criteria.passenger ?? {}),
        siteId: { $in: customProductionFormData.siteIds },
      };
    }

    if (customProductionFormData.passengerTypeId) {
      criteria.passenger = {
        ...(criteria.passenger ?? {}),
        passengerTypeId: { $eq: customProductionFormData.passengerTypeId },
      };
    }

    if (customProductionFormData.areaIds?.length) {
      criteria.stationArea = { $any: customProductionFormData.areaIds };
    }

    const summaryItems = await buildProduceSummaryForCustomProductionDialog(
      customProductionFormData,
      bookingDates,
    );

    setBookingsProductionDrawerConfiguration({
      action: customProductionFormData.productionAction,
      productionType: customProductionFormData.productionType,
      rideTypeId: customProductionFormData.rideTypeId,
      assignDocksDuringProduction:
        customProductionFormData.assignDocksDuringProduction,
      ...resolveProduceDirectionsServicePayload(
        customProductionFormData.directionsService,
      ),
      criteria,
      summaryItems,
    });

    if (companyId) {
      storeBookingsProductionAction({
        companyId,
        action: customProductionFormData.productionAction,
      });
    }

    setCurrentStep("producePreview");
  }, [
    bookingDates,
    bookingsIds,
    buildProduceSummaryForCustomProductionDialog,
    companyId,
    getValues,
    setBookingsProductionDrawerConfiguration,
    setCurrentStep,
    trigger,
  ]);

  useEffect(() => {
    setHandlers({
      onCloseHandler,
      onSaveClick,
    });
  }, [onCloseHandler, onSaveClick, setHandlers]);

  return (
    <DrawerContentStyled className="drawer-form drawer-form-with-image">
      <FormProvider {...methods}>
        {!bookingsIds.length && (
          <ErrorsDisplayContainerStyled>
            <ErrorsDisplayItem
              errorDescription={t("booking.bulk.noBookingsSelected")}
            />
          </ErrorsDisplayContainerStyled>
        )}
        <BookingsProductionDrawerConfigurationForm />
      </FormProvider>
    </DrawerContentStyled>
  );
};
